import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { FormLayout } from '../common/index.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import { Plus, Filter, CheckCircle, ArrowLeft, Pencil, Trash2, MoreVertical, HandCoins, XCircle, CreditCard, Eye, EyeOff, X } from "lucide-react";

const EditLoanReceived = () => {
  const { loanId } = useParams();
  const [formData, setFormData] = useState({
    lender_name: '',
    lender_contact: '',
    lender_email: '',
    lender_address: '',
    account_number: '',
    ifsc_code: '',
    interest_rate: '',
    processing_fee: '',
    due_date: '',
    loan_account_number: '',
    collateral_details: '',
    purpose: '',
    remarks: ''
  });
  
  const [loanAgreement, setLoanAgreement] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [existingAgreement, setExistingAgreement] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loanInfo, setLoanInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLoan();
  }, [loanId]);

  const fetchLoan = async () => {
    try {
      const response = await loanReceivedService.getById(loanId);
      const loan = response.data;
      
      setLoanInfo({
        loan_id: loan.loan_id,
        lender_type: loan.lender_type,
        loan_type: loan.loan_type,
        loan_amount: loan.loan_amount,
        total_amount: loan.total_amount,
        amount_paid: loan.amount_paid,
        amount_pending: loan.amount_pending,
        status: loan.status,
        loan_date: loan.loan_date
      });
      
      setFormData({
        lender_name: loan.lender_name || '',
        lender_contact: loan.lender_contact || '',
        lender_email: loan.lender_email || '',
        lender_address: loan.lender_address || '',
        account_number: loan.account_number || '',
        ifsc_code: loan.ifsc_code || '',
        interest_rate: loan.interest_rate?.toString() || '0',
        processing_fee: loan.processing_fee?.toString() || '0',
        due_date: loan.due_date || '',
        loan_account_number: loan.loan_account_number || '',
        collateral_details: loan.collateral_details || '',
        purpose: loan.purpose || '',
        remarks: loan.remarks || ''
      });
      
      setExistingAgreement(loan.has_agreement);
    } catch (err) {
      Alert.error('Failed to load loan data');
      console.error(err);
      navigate('/loans-received');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setLoanAgreement(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setDocumentPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setDocumentPreview('pdf');
      }
    }
  };

  const removeDocument = () => {
    setLoanAgreement(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('agreement-input');
    if (fileInput) fileInput.value = '';
  };

  const viewExistingAgreement = () => {
    const documentUrl = loanReceivedService.getLoanAgreement(loanId);
    window.open(documentUrl, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await loanReceivedService.update(loanId, formData, loanAgreement);
      Alert.success('Loan updated successfully!');
      setTimeout(() => navigate('/loans-received'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update loan');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <FormLayout title="Edit Loan Received" icon={CreditCard} maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading...</p>
          </div>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Edit Loan Received"
      icon={CreditCard}
      onSubmit={handleSubmit}
      submitLabel={submitting ? 'Updating...' : 'Update Loan'}
      loading={submitting}
      onCancel={() => navigate('/loans-received')}
      maxWidth="max-w-7xl"
    >

                  {/* Section 1: Loan Summary */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Loan Summary
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-brand-light p-3 sm:p-4 rounded-sm">
                        <label className="block text-xs text-gray-600 mb-1">Loan Amount</label>
                        <p className="text-base sm:text-lg font-bold text-brand">{formatCurrency(loanInfo?.loan_amount)}</p>
                      </div>
                      <div className="bg-green-50 p-3 sm:p-4 rounded-sm">
                        <label className="block text-xs text-gray-600 mb-1">Total Amount</label>
                        <p className="text-base sm:text-lg font-bold text-green-700">{formatCurrency(loanInfo?.total_amount)}</p>
                      </div>
                      <div className="bg-blue-50 p-3 sm:p-4 rounded-sm">
                        <label className="block text-xs text-gray-600 mb-1">Amount Paid</label>
                        <p className="text-base sm:text-lg font-bold text-blue-700">{formatCurrency(loanInfo?.amount_paid)}</p>
                      </div>
                      <div className="bg-red-50 p-3 sm:p-4 rounded-sm">
                        <label className="block text-xs text-gray-600 mb-1">Pending</label>
                        <p className="text-base sm:text-lg font-bold text-red-700">{formatCurrency(loanInfo?.amount_pending)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Lender Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Lender Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Lender Name <span class="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          name="lender_name"
                          value={formData.lender_name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Contact Number <span class="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="tel"
                          name="lender_contact"
                          value={formData.lender_contact}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          name="lender_email"
                          value={formData.lender_email}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Account Number
                        </label>
                        <input
                          type="text"
                          name="account_number"
                          value={formData.account_number}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          IFSC Code
                        </label>
                        <input
                          type="text"
                          name="ifsc_code"
                          value={formData.ifsc_code}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Address
                        </label>
                        <textarea
                          name="lender_address"
                          value={formData.lender_address}
                          onChange={handleChange}
                          rows="2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Loan Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Loan Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Interest Rate (% per annum)
                        </label>
                        <input
                          type="number"
                          name="interest_rate"
                          value={formData.interest_rate}
                          onChange={handleChange}
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Processing Fee
                        </label>
                        <input
                          type="number"
                          name="processing_fee"
                          value={formData.processing_fee}
                          onChange={handleChange}
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Due Date <span class="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="date"
                          name="due_date"
                          value={formData.due_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Loan Account Number
                        </label>
                        <input
                          type="text"
                          name="loan_account_number"
                          value={formData.loan_account_number}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Additional Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Additional Information
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Loan Purpose <span class="text-red-500 ml-1">*</span>
                        </label>
                        <textarea
                          name="purpose"
                          value={formData.purpose}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Collateral Details
                        </label>
                        <textarea
                          name="collateral_details"
                          value={formData.collateral_details}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Loan Agreement Document
                        </label>
                        
                        {existingAgreement && !documentPreview && (
                          <button
                            type="button"
                            onClick={viewExistingAgreement}
                            className="mb-3 px-4 py-2 bg-brand text-white rounded-sm hover:bg-brand transition-colors text-sm font-medium flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Existing Agreement
                          </button>
                        )}
                        
                        <input
                          id="agreement-input"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleDocumentChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          {existingAgreement ? 'Upload new document to replace existing one (Max 5MB)' : 'Upload loan agreement document (Max 5MB)'}
                        </p>
                        
                        {documentPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">New Document Preview:</p>
                            {documentPreview === 'pdf' ? (
                              <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-sm">
                                <span className="text-2xl">📄</span>
                                <span className="text-sm text-gray-700">{loanAgreement.name}</span>
                                <button
                                  type="button"
                                  onClick={removeDocument}
                                  className="ml-auto text-red-500 hover:text-red-600"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="relative inline-block">
                                <img 
                                  src={documentPreview} 
                                  alt="Document Preview" 
                                  className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                                />
                                <button
                                  type="button"
                                  onClick={removeDocument}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Remarks
                        </label>
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

    </FormLayout>
  );
};

export default EditLoanReceived;